"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, cx, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { Kbd, KbdGroup } from "./kbd";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";

const primaryVariant = [
  "[--button-color-border:var(--button-color-bg)] [--button-color-bg:var(--color-gray-900)] [--button-color-ring:var(--color-gray-900)] [--button-color-text:var(--color-white)] [--button-text-shadow:0px_1px_1px_var(--color-gray-900)] active:[--button-color-bg:var(--color-gray-800)] dark:[--button-color-border:rgb(229_229_230)] dark:[--button-color-bg:rgb(229_229_230)] dark:[--button-color-ring:rgb(0_0_0/0.08)] dark:[--button-color-text:var(--color-gray-900)] dark:[--button-text-shadow:none] dark:hover:[--button-color-border:var(--color-white)] dark:hover:[--button-color-bg:var(--color-white)] dark:active:[--button-color-bg:rgb(229_229_230)]",
  "relative shadow-[inset_0_1px_rgb(255_255_255/7%),0_1px_3px_rgb(17_24_39/20%)] hover:before:opacity-25 dark:shadow-none dark:ring-3 dark:ring-transparent dark:ring-offset-[0.0625rem] dark:ring-offset-[var(--button-color-border)]",
  "before:absolute before:inset-0 before:rounded-[inherit] dark:hover:before:opacity-0",
] as const;

const secondaryVariant = [
  "rounded-sm",
  "[--button-color-border:rgb(0_0_0/0.08)] [--button-color-bg:var(--color-white)] [--button-color-icon:var(--text-tertiary)] [--button-color-icon-hover:var(--text-primary)] [--button-color-ring:rgb(0_0_0/0.08)] [--button-color-text:var(--text-primary)] hover:[--button-color-bg:var(--color-gray-50)] active:[--button-color-bg:var(--color-gray-100)] dark:[--button-color-border:var(--color-gray-700)] dark:[--button-color-bg:var(--color-gray-800)] dark:[--button-color-ring:var(--color-gray-600)] dark:hover:[--button-color-bg:var(--color-gray-800)] dark:active:[--button-color-bg:var(--color-gray-800)]",
  "ring-3 ring-transparent ring-offset-[0.0625rem] ring-offset-[var(--button-color-border)]",
  "before:absolute before:inset-0 before:rounded-[inherit] before:bg-linear-to-b before:from-black/0 before:from-50% before:to-black/[0.02] before:transition-opacity",
] as const;

const ghostVariant = [
  "[--button-color-border:var(--button-color-bg)] [--button-color-bg:transparent] [--button-color-icon:var(--text-tertiary)] [--button-color-icon-hover:var(--text-secondary)] [--button-color-ring:rgb(0_0_0/0.08)] [--button-color-text:var(--text-secondary)] hover:[--button-color-bg:var(--color-gray-100)] hover:[--button-color-text:var(--text-primary)] active:[--button-color-bg:var(--color-gray-100)] data-[state=open]:[--button-color-bg:var(--color-gray-100)] dark:data-[state=open]:[--button-color-bg:var(--color-zinc-700)] dark:hover:[--button-color-bg:var(--color-zinc-700)] dark:active:[--button-color-bg:var(--color-zinc-700)] dark:[--button-color-text:var(--color-zinc-200)]",
  "focus-visible:[--button-color-border:rgb(0_0_0/0.15)]",
] as const;

const buttonVariants = cva(
  [
    "group relative inline-flex select-none items-center justify-center overflow-hidden rounded-sm bg-[var(--button-color-bg)] font-medium text-[var(--button-color-text)] text-balance outline-none transition cursor-pointer ",
    "before:bg-[radial-gradient(75%_75%_at_center_top,rgb(255_255_255/20%),transparent)] before:transition after:transition",
    "focus-visible:ring-[var(--button-color-ring)] disabled:cursor-not-allowed",
  ],
  {
    variants: {
      variant: {
        primary: primaryVariant,
        default: primaryVariant,
        orange: [
          "[--button-color-border:var(--button-color-bg)] [--button-color-bg:var(--color-orange-500)] [--button-color-ring:rgb(249_115_22/0.2)] [--button-color-text:var(--color-white)] [--button-text-shadow:0px_1px_1px_var(--color-orange-700)]",
          "relative shadow-[inset_0px_1px_0px_rgb(255_255_255/8%),inset_0px_-1px_0px_rgb(255_255_255/4%),0px_0px_0px_1px_var(--color-orange-500),0px_2px_2px_-1px_rgb(124_45_18/24%),0px_4px_4px_-2px_rgb(124_45_18/12%)] hover:before:opacity-25 active:[--button-color-bg:var(--color-orange-600)] before:absolute before:inset-0 before:rounded-[inherit]",
        ],
        action: [
          "[--button-color-border:var(--button-color-bg)] [--button-color-bg:var(--color-cyan-400)] [--button-color-ring:rgb(6_182_212/0.2)] [--button-color-text:var(--color-cyan-950)] [--button-text-shadow:0px_1px_0px_rgb(255_255_255/20%)]",
          "relative shadow-[inset_0px_0px_0px_1px_rgb(255_255_255/6%),inset_0px_1px_0px_rgb(255_255_255/6%),inset_0px_-1px_0px_rgb(255_255_255/4%),0px_0px_0px_1px_var(--color-cyan-400),0px_2px_2px_-1px_rgb(22_78_99/24%),0px_4px_4px_-2px_rgb(22_78_99/12%)] hover:before:opacity-25 before:absolute before:inset-0 before:rounded-[inherit]",
        ],
        secondary: secondaryVariant,
        outline: secondaryVariant,
        ghost: ghostVariant,
        "ghost-muted": ghostVariant,
        destructive: [
          "[--button-color-border:var(--button-color-bg)] [--button-color-bg:var(--color-red-500)] [--button-color-ring:rgb(239_68_68/0.2)] [--button-color-text:var(--color-white)] [--button-text-shadow:0px_1px_3px_rgb(0_0_0/25%)]",
          "relative shadow-[0px_2px_3px_rgb(31_41_55/20%),0px_0px_0px_1px_var(--color-red-500),inset_0px_1px_0px_rgb(255_255_255/7%)] hover:before:opacity-25 active:[--button-color-bg:var(--color-red-600)] before:absolute before:inset-0 before:rounded-[inherit]",
        ],
        "destructive-outline": secondaryVariant,
        glass: secondaryVariant,
        link: "[--button-color-bg:transparent] [--button-color-text:var(--text-secondary)] underline-offset-4 hover:underline",
      },
      size: {
        xxs: "h-7 px-2 text-xs",
        xs: "h-7 px-2 text-xs",
        sm: "h-6 px-2 py-1 text-sm",
        compact: "h-7 px-2 text-xs",
        default: "h-7 px-2.5 py-2 text-[13px]",
        lg: "px-5 py-3 text-base",
        xl: "h-11 px-5 text-lg",
        icon: "size-7",
        "icon-sm": "size-6",
        "icon-xs": "size-6",
        "icon-micro": "size-5",
        "icon-lg": "size-9",
        "icon-xl": "size-10",
        playground: "rounded-full px-4 py-2 text-lg font-semibold",
      },
      radius: {
        full: "rounded-full",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  },
);

interface ButtonProps extends useRender.ComponentProps<"button"> {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  radius?: VariantProps<typeof buttonVariants>["radius"];
  wrapperClassName?: string;
  loadingClassName?: string;
  tooltip?: string;
  tooltipAlign?: "start" | "center" | "end";
  tooltipVariant?: "default" | "dark";
  shortcut?: string | ReadonlyArray<string>;
  isLoading?: boolean;
}

function TypingLoader({ className }: { className?: string }) {
  return (
    <span className={cn("flex h-4 items-center gap-1", className)}>
      {[0, 1, 2].map((index) => (
        <span
          className="size-1 animate-[typing_1s_infinite] rounded-full bg-current"
          key={index}
          style={{ animationDelay: `${index * 250}ms` }}
        />
      ))}
    </span>
  );
}

function Button({
  className,
  variant,
  size,
  radius,
  render,
  children,
  wrapperClassName,
  loadingClassName,
  tooltip,
  tooltipAlign,
  tooltipVariant = "default",
  shortcut,
  isLoading,
  ...props
}: ButtonProps) {
  const content = (
    <span className="relative block">
      <span
        className={cn(
          cx(
            "flex w-full items-center justify-center gap-1.5 whitespace-nowrap drop-shadow-[var(--button-text-shadow)] transition-[transform,opacity] duration-50",
            isLoading && "-translate-y-5 opacity-0",
            wrapperClassName,
          ),
        )}
      >
        {children}
      </span>
      <span
        className={cn(
          "absolute inset-0 flex translate-y-5 items-center justify-center opacity-0 transition-[transform,opacity] duration-200",
          isLoading && "translate-y-0 opacity-100",
        )}
      >
        <TypingLoader
          {...(loadingClassName === undefined ? {} : { className: loadingClassName })}
        />
      </span>
    </span>
  );
  const button = useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(
      {
        className: cn(buttonVariants({ className, size, variant, radius })),
        type: render ? undefined : "button",
        children: content,
      },
      props,
    ),
    render,
  });

  if (tooltip === undefined) return button;
  return (
    <Tooltip>
      <TooltipTrigger id={props.id} render={button} />
      <TooltipPopup align={tooltipAlign} sideOffset={10} variant={tooltipVariant}>
        <span className="flex items-center gap-2">
          {tooltip}
          {shortcut ? (
            <KbdGroup>
              {(typeof shortcut === "string" ? [shortcut] : shortcut).map((key) => (
                <Kbd key={key}>{key}</Kbd>
              ))}
            </KbdGroup>
          ) : null}
        </span>
      </TooltipPopup>
    </Tooltip>
  );
}

export type { ButtonProps };
export { Button, buttonVariants };
